/**
 * AR Touch Controller
 * Handles touch interactions specifically for AR mode
 * Based on model-viewer's AR interaction patterns
 */

export class ARTouchController {
    constructor(canvas, renderer) {
        this.canvas = canvas;
        this.renderer = renderer;
        this.enabled = false;
        
        // Model transform
        this.modelPosition = { x: 0, y: 0, z: -3 }; // 3 meters in front
        this.modelRotation = { x: 0, y: 0, z: 0 };
        this.modelScale = 1.0;
        
        // Touch state
        this.touches = new Map();
        this.lastTouchDistance = 0;
        this.lastTouchAngle = 0;
        this.isDragging = false;
        
        // Virtual ground plane (y = 0)
        this.groundPlane = { normal: { x: 0, y: 1, z: 0 }, point: { x: 0, y: 0, z: 0 } };
        
        // Bind event handlers
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.onDoubleTap = this.onDoubleTap.bind(this);
        
        this.lastTapTime = 0;
        this.doubleTapDelay = 300; // ms
    }
    
    enable() {
        if (this.enabled) return;
        this.enabled = true;
        
        this.canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.canvas.addEventListener('touchend', this.onTouchEnd, { passive: false });
        this.canvas.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
    }
    
    disable() {
        if (!this.enabled) return;
        this.enabled = false;
        
        this.canvas.removeEventListener('touchstart', this.onTouchStart);
        this.canvas.removeEventListener('touchmove', this.onTouchMove);
        this.canvas.removeEventListener('touchend', this.onTouchEnd);
        this.canvas.removeEventListener('touchcancel', this.onTouchEnd);
    }
    
    onTouchStart(e) {
        e.preventDefault();
        
        // Store touches
        for (const touch of e.touches) {
            this.touches.set(touch.identifier, {
                x: touch.clientX,
                y: touch.clientY,
                startX: touch.clientX,
                startY: touch.clientY
            });
        }
        
        // Check for double tap
        const now = Date.now();
        if (e.touches.length === 1 && now - this.lastTapTime < this.doubleTapDelay) {
            this.onDoubleTap(e);
            return;
        }
        this.lastTapTime = now;
        
        // Initialize gesture state
        if (e.touches.length === 2) {
            const [touch1, touch2] = Array.from(this.touches.values());
            this.lastTouchDistance = this.getTouchDistance(touch1, touch2);
            this.lastTouchAngle = this.getTouchAngle(touch1, touch2);
        }
        
        this.isDragging = true;
    }
    
    onTouchMove(e) {
        e.preventDefault();
        
        if (!this.isDragging) return;
        
        // Update touch positions
        for (const touch of e.touches) {
            if (this.touches.has(touch.identifier)) {
                const storedTouch = this.touches.get(touch.identifier);
                storedTouch.x = touch.clientX;
                storedTouch.y = touch.clientY;
            }
        }
        
        if (e.touches.length === 1) {
            // Single finger: move model on ground plane
            this.handleModelMove(e.touches[0]);
        } else if (e.touches.length === 2) {
            // Two fingers: scale and rotate
            const [touch1, touch2] = Array.from(this.touches.values());
            this.handleScaleAndRotate(touch1, touch2);
        }
    }
    
    onTouchEnd(e) {
        e.preventDefault();
        
        // Remove ended touches
        for (const touch of e.changedTouches) {
            this.touches.delete(touch.identifier);
        }
        
        if (e.touches.length === 0) {
            this.isDragging = false;
            this.touches.clear();
        }
    }
    
    onDoubleTap(e) {
        // Reset model to default position
        this.modelPosition = { x: 0, y: 0, z: -3 };
        this.modelRotation = { x: 0, y: 0, z: 0 };
        this.modelScale = 1.0;
        this.updateModelTransform();
    }
    
    handleModelMove(touch) {
        // Cast ray from touch point to ground plane
        const rect = this.canvas.getBoundingClientRect();
        const x = ((touch.clientX - rect.left) / rect.width) * 2 - 1;
        const y = -((touch.clientY - rect.top) / rect.height) * 2 + 1;
        
        // Simple ray-plane intersection
        // In a real implementation, this would use the camera's projection matrix
        // For now, we'll use a simplified approach
        const deltaX = x * 0.01;
        const deltaZ = y * 0.01;
        
        this.modelPosition.x += deltaX;
        this.modelPosition.z += deltaZ;
        
        // Keep model on ground
        this.modelPosition.y = 0;
        
        this.updateModelTransform();
    }
    
    handleScaleAndRotate(touch1, touch2) {
        const distance = this.getTouchDistance(touch1, touch2);
        const angle = this.getTouchAngle(touch1, touch2);
        
        // Scale based on pinch
        if (this.lastTouchDistance > 0) {
            const scaleDelta = distance / this.lastTouchDistance;
            this.modelScale *= scaleDelta;
            this.modelScale = Math.max(0.1, Math.min(5.0, this.modelScale));
        }
        
        // Rotate based on two-finger rotation
        if (this.lastTouchAngle !== null) {
            const angleDelta = angle - this.lastTouchAngle;
            this.modelRotation.y += angleDelta;
        }
        
        this.lastTouchDistance = distance;
        this.lastTouchAngle = angle;
        
        this.updateModelTransform();
    }
    
    getTouchDistance(touch1, touch2) {
        const dx = touch2.x - touch1.x;
        const dy = touch2.y - touch1.y;
        return Math.sqrt(dx * dx + dy * dy);
    }
    
    getTouchAngle(touch1, touch2) {
        return Math.atan2(touch2.y - touch1.y, touch2.x - touch1.x);
    }
    
    updateModelTransform() {
        // Update the renderer's model transform
        if (this.renderer && this.renderer.setModelTransform) {
            this.renderer.setModelTransform({
                position: this.modelPosition,
                rotation: this.modelRotation,
                scale: { x: this.modelScale, y: this.modelScale, z: this.modelScale }
            });
        }
        
        // Emit event for other systems
        if (this.renderer && this.renderer.emit) {
            this.renderer.emit('arModelTransformChanged', {
                position: this.modelPosition,
                rotation: this.modelRotation,
                scale: this.modelScale
            });
        }
    }
    
    // Get current model transform
    getModelTransform() {
        return {
            position: { ...this.modelPosition },
            rotation: { ...this.modelRotation },
            scale: this.modelScale
        };
    }
    
    // Set initial model position (e.g., after plane detection)
    setModelPosition(position) {
        this.modelPosition = { ...position };
        this.updateModelTransform();
    }
}